import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { FaTimes } from 'react-icons/fa';
import './ModalTokens.css';
import './AppModal.css';

/**
 * Global AppModal Component
 * Implements the "Enterprise Blue" design system.
 * 
 * @param {boolean} isOpen - Whether the modal is visible
 * @param {function} onClose - Function to call when closing (via ESC, click outside, or X)
 * @param {string} title - The modal title (Required)
 * @param {ReactNode} children - The body content
 * @param {ReactNode} footer - The footer content (buttons)
 * @param {string} size - 'sm' | 'md' | 'lg' | 'xl' | 'full' (default: 'md')
 * @param {boolean} preventClose - If true, prevents closing via overlay click or ESC
 */
const AppModal = ({
    isOpen,
    onClose,
    title,
    children,
    footer,
    size = 'md',
    preventClose = false,
}) => {
    const modalRef = useRef(null);
    const previousFocus = useRef(null);

    // Focus Trapping and Scroll Locking
    useEffect(() => {
        if (isOpen) {
            // Store previous focus
            previousFocus.current = document.activeElement;

            // Lock body scroll
            document.body.style.overflow = 'hidden';

            // Focus the modal container for accessibility
            // Simple timeout ensures mount is complete
            setTimeout(() => {
                if (modalRef.current) {
                    modalRef.current.focus();
                }
            }, 50);

            return () => {
                document.body.style.overflow = '';
                // Restore focus
                if (previousFocus.current) {
                    previousFocus.current.focus();
                }
            };
        }
    }, [isOpen]);

    // Keyboard Events
    useEffect(() => {
        if (isOpen) {
            const handleKeyDown = (e) => {
                if (e.key === 'Escape' && !preventClose) {
                    onClose();
                }
            };

            window.addEventListener('keydown', handleKeyDown);

            return () => {
                window.removeEventListener('keydown', handleKeyDown);
            };
        }
    }, [isOpen, onClose, preventClose]);

    // Handle Backdrop Click
    const handleBackdropClick = (e) => {
        if (e.target === e.currentTarget && !preventClose) {
            onClose();
        }
    };

    if (!isOpen) return null;

    return ReactDOM.createPortal(
        <div className="app-modal-overlay" onClick={handleBackdropClick}>
            <div
                className={`app-modal-container app-modal-${size}`}
                role="dialog"
                aria-modal="true"
                aria-labelledby="modal-title"
                tabIndex="-1"
                ref={modalRef}
            >
                <div className="app-modal-header">
                    <h3 id="modal-title" className="app-modal-title">{title}</h3>
                    <button
                        type="button"
                        className="app-modal-close"
                        onClick={onClose}
                        aria-label="Close"
                    >
                        <FaTimes size={18} />
                    </button>
                </div>

                <div className="app-modal-body">
                    {children}
                </div>

                {footer && (
                    <div className="app-modal-footer">
                        {footer}
                    </div>
                )}
            </div>
        </div>,
        document.body
    );
};

export default AppModal;
